import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Trash2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact comments block for a published game — used in Gallery cards.
 * Anyone can read comments; only signed-in users can post; admins + owners can delete.
 */
export default function CommentsBlock({ projectId }) {
  const { user, isAuthenticated } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/gallery/${projectId}/comments`);
      setItems(r.data?.comments || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open) load(); }, [open, projectId]);

  const post = async () => {
    if (!isAuthenticated) { toast.info('Sign in to comment'); return; }
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/gallery/${projectId}/comments`, { text: t }, { withCredentials: true });
      setItems(prev => [r.data.comment, ...prev]);
      setText('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Comment failed');
    } finally { setBusy(false); }
  };

  const del = async (cid) => {
    if (!window.confirm('Delete comment?')) return;
    try {
      await axios.delete(`${API}/gallery/comments/${cid}`, { withCredentials: true });
      setItems(prev => prev.filter(c => c.comment_id !== cid));
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="mt-2" data-testid={`comments-${projectId}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className="text-[10px] font-mono text-zinc-500 hover:text-[#06B6D4] flex items-center gap-1"
        data-testid={`comments-toggle-${projectId}`}
      >
        <MessageCircle className="w-3 h-3" /> {open ? 'Hide' : 'Comments'} ({items.length || 0})
      </button>
      {open && (
        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto pr-1">
          {items.length === 0 && <p className="text-[10px] text-zinc-600">Be the first to comment.</p>}
          {items.map(c => (
            <div key={c.comment_id} className="text-[11px] text-zinc-300 bg-[#121214] border border-[#1E1E24] rounded px-2 py-1">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[10px] text-[#06B6D4] font-mono">{c.author_name || 'user'}{c.author_role === 'admin' && ' · admin'}</span>
                {(user?.role === 'admin' || user?.user_id === c.user_id) && (
                  <button onClick={() => del(c.comment_id)} className="text-zinc-600 hover:text-red-400">
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
              <p className="whitespace-pre-wrap break-words">{c.text}</p>
            </div>
          ))}
          <div className="flex gap-1 pt-1">
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && post()}
              placeholder="Say something…"
              className="flex-1 h-7 text-[11px] bg-transparent border border-[#1E1E24] rounded px-2 text-white outline-none focus:border-[#06B6D4]"
              disabled={busy}
              data-testid={`comment-input-${projectId}`}
            />
            <button
              onClick={post}
              disabled={busy || !text.trim()}
              className="w-7 h-7 rounded bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90 flex items-center justify-center disabled:opacity-50"
              data-testid={`comment-send-${projectId}`}
            >
              <Send className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
